import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const EmailOTPModal = ({ open, onClose, redirectPath = null }) => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('email'); // 'email' or 'otp'
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [pendingRedirect, setPendingRedirect] = useState(null);
  const { toast } = useToast();

  // Timer countdown
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleSendOTP = async () => {
    if (!email || !email.includes('@')) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setStep('otp');
        setTimer(60); // 60 seconds countdown
        toast({
          title: "OTP Sent!",
          description: `We've sent a verification code to ${email}`,
        });
      } else {
        toast({
          title: "Error",
          description: data.detail || "Failed to send OTP. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast({
        title: "Error",
        description: "Failed to send OTP. Please check your connection.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast({
        title: "Invalid OTP",
        description: "Please enter the 6-digit code.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp }),
      });

      const data = await response.json();

      if (response.ok) {
        // Sign in with custom token
        const { auth } = await import('../firebase/config');
        const { signInWithCustomToken } = await import('firebase/auth');
        
        await signInWithCustomToken(auth, data.customToken);
        
        toast({
          title: "Welcome!",
          description: "You've been signed in successfully.",
        });
        
        // Store redirect path before closing
        const redirectTo = redirectPath;
        
        // Reset and close
        handleClose();
        
        // Redirect to specified path after successful login
        if (redirectTo) {
          setTimeout(() => {
            window.location.href = redirectTo;
          }, 100);
        }
      } else {
        toast({
          title: "Verification Failed",
          description: data.detail || "Invalid or expired OTP. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast({
        title: "Error",
        description: "Failed to verify OTP. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setOtp('');
    setStep('email');
    setTimer(0);
    onClose();
  };

  const handleBack = () => {
    setStep('email');
    setOtp('');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent">
            {step === 'email' ? 'Sign in with Email' : 'Verify OTP'}
          </DialogTitle>
          <DialogDescription className="text-center text-gray-600">
            {step === 'email' 
              ? 'Enter your email to receive a verification code'
              : `Enter the 6-digit code sent to ${email}`
            }
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {step === 'email' ? (
            // Email Input Step
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    onKeyPress={(e) => e.key === 'Enter' && handleSendOTP()}
                  />
                </div>
              </div>

              <Button
                onClick={handleSendOTP}
                disabled={loading}
                className="w-full bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  'Send Verification Code'
                )}
              </Button>
            </div>
          ) : (
            // OTP Input Step
            <div className="space-y-4">
              <div>
                <Label htmlFor="otp">Verification Code</Label>
                <Input
                  id="otp"
                  type="text"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-widest font-bold"
                  maxLength={6}
                  onKeyPress={(e) => e.key === 'Enter' && handleVerifyOTP()}
                />
                {timer > 0 && (
                  <p className="text-sm text-gray-500 mt-2 text-center">
                    Resend code in {timer}s
                  </p>
                )}
              </div>

              <Button
                onClick={handleVerifyOTP}
                disabled={loading || otp.length !== 6}
                className="w-full bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Sign In'
                )}
              </Button>

              {timer === 0 && (
                <Button
                  onClick={handleSendOTP}
                  disabled={loading}
                  variant="outline"
                  className="w-full"
                >
                  Resend Code
                </Button>
              )}

              <Button
                onClick={handleBack}
                variant="ghost"
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Change Email
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailOTPModal;
